import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { decode } from 'he';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import {
  GET_PUBLIC_SITE,
  GET_TINYCMS_ARTICLES,
  GET_TINYCMS_SITE,
  UPDATE_PUBLIC_SITE,
  GET_TINYCMS_SEARCH,
  UPDATE_TINYCMS_SITE,
  CREATE_TINYCMS_PREVIEW,
  PUBLISH_TINYCMS_CONTENT,
  TINYCMS_SITE_CHANGES,
  DISCARD_TINYCMS_SITE_CHANGES,
  GET_TINYCMS_APPROVALS,
  GET_TINYCMS_PAGES,
  GET_TINYCMS_FORMS,
} from '../../graphql';
import {
  APPLY_TINYCMS_STARTER,
  StarterStep,
  TINYCMS_STARTERS,
  useTemplateFonts,
  type Starter,
} from '../account/StarterStep';
import { fontStack } from '../account/StarterThumb';
import { StarterThumb } from '../account/StarterThumb';
import { SitePreviewFrame, previewAddresses } from './SitePreviewFrame';
import { DomainConnect } from './DomainConnect';
import { SearchConsole } from './SearchConsole';
import { SettingsHistory } from '../settings/SettingsHistory';
import { ApprovalHistory, Finding, InkSection, type SiteChange } from './shared';
import { Loader } from '../common/PageLoader';
import { NOT_A_LOGIN, PasswordInput } from '../common/PasswordInput';
import { CssChecks } from './CssChecks';
import { publishChecks, tagOf, type PublishAction } from './publishChecks';
import { showInkToast } from './InkToast';
import { whatChanged } from './changeGroups';
import { WhatChanged } from './WhatChanged';
import {
  discardResultText,
  lookPagesConfirm,
  lookPagesText,
  lookText,
  type TemplateAdded,
} from './templateWords';
import { approverPhrase } from './approver';
import { askInk } from './InkConfirm';
import { useToday } from './todayData';
import { bundleItems, usePublishTogether, type BundleItem } from './publishTogether';

/** The live version's look, or null before the first publish. */
const TINYCMS_LIVE_LOOK = gql`
  query TinycmsLiveLook($accountId: ID!) {
    tinycmsLiveLook(accountId: $accountId)
  }
`;

/** Back to the live look: the way out after trying a template. */
const REVERT_TINYCMS_SITE_THEME = gql`
  mutation RevertTinycmsSiteTheme($accountId: ID!) {
    revertTinycmsSiteTheme(accountId: $accountId)
  }
`;

interface LiveLook {
  version: number;
  starter: { id: string; appliedAt?: string } | null;
}

/**
 * The site, in two Content → Manage areas (UX optimization review, 2026-10-24):
 *   design    Manage → Design: the theme, what agents may change, history
 *   settings  Manage → Site: visibility, search engines, identity, domain and
 *             the page's history. Analytics & code and Distribution are their
 *             own areas (ManageView).
 * Visibility and Search engines take effect immediately. Theme edits (and the logo, cover and
 * icon) stay on the working copy until Publish. Domain and analytics keep their
 * own saves.
 */

const COLOR_KEYS = ['background', 'text', 'accent', 'muted'] as const;
const COLOR_LABELS: Record<(typeof COLOR_KEYS)[number], string> = {
  background: 'Background',
  text: 'Text',
  accent: 'Links & accent',
  muted: 'Secondary text',
};
const HEX = /^#[0-9a-f]{6}$/i;
const SURFACES: Array<{ key: string; name: string; desc: string; def: number }> = [
  { key: 'home', name: 'Homepage layout', desc: 'Modules, order, featured stories', def: 1 },
  { key: 'hubs', name: 'Topic hubs', desc: 'Landing pages for each topic', def: 0 },
  { key: 'nav', name: 'Navigation', desc: 'Header and footer links', def: 1 },
  { key: 'theme', name: 'Theme', desc: 'Type, color, logo', def: 2 },
  { key: 'public', name: 'Public fields', desc: 'Which custom fields readers can see', def: 2 },
];
/** "today 8:43 AM", "Sep 28" — when a surface last changed. */
function changedWhen(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toDateString() === new Date().toDateString()
    ? `today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PERM_LABELS = [
  { label: 'Agents can edit', bg: 'var(--ink-green-bg)', color: 'var(--ink-green)' },
  { label: 'Ask first', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' },
  { label: 'Locked', bg: 'var(--ink-bg3)', color: 'var(--ink-ink2)' },
];

interface SiteForm {
  preset: string;
  colors: Record<string, string>;
  fonts: { heading: string; body: string };
  layout: { home: string; width: string; corners: string; photos: string };
  logoUrl: string;
  logoAlt: string;
  coverUrl: string;
  coverAlt: string;
  iconUrl: string;
}

const toForm = (resolved: any): SiteForm => ({
  preset: resolved?.preset || 'classic',
  colors: { ...(resolved?.colors || {}) },
  // A Google Fonts choice is stored as { google, category, weights }; the form
  // holds its family name, so an untouched font is never re-sent.
  fonts: {
    heading: fontName(resolved?.fonts?.heading) || 'serif',
    body: fontName(resolved?.fonts?.body) || 'sans',
  },
  layout: {
    home: resolved?.layout?.home || 'feed',
    width: resolved?.layout?.width || 'wide',
    corners: resolved?.layout?.corners || 'round',
    photos: resolved?.layout?.photos || 'colour',
  },
  logoUrl: resolved?.imagery?.logo?.url || '',
  logoAlt: resolved?.imagery?.logo?.alt || '',
  coverUrl: resolved?.imagery?.cover?.url || '',
  coverAlt: resolved?.imagery?.cover?.alt || '',
  iconUrl: resolved?.imagery?.icon?.url || '',
});

function fontName(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && typeof (v as { google?: unknown }).google === 'string') {
    return (v as { google: string }).google;
  }
  return null;
}

function diff(a: SiteForm, b: SiteForm) {
  const patch: Record<string, any> = {};
  if (a.preset !== b.preset) patch.preset = b.preset;
  const colors: Record<string, string> = {};
  for (const k of COLOR_KEYS)
    if ((a.colors[k] || '') !== (b.colors[k] || '')) colors[k] = b.colors[k];
  if (Object.keys(colors).length) patch.colors = colors;
  const fonts: Record<string, string> = {};
  if (a.fonts.heading !== b.fonts.heading) fonts.heading = b.fonts.heading;
  if (a.fonts.body !== b.fonts.body) fonts.body = b.fonts.body;
  if (Object.keys(fonts).length) patch.fonts = fonts;
  const layout: Record<string, any> = {};
  if (a.layout.home !== b.layout.home) layout.home = b.layout.home;
  if (a.layout.width !== b.layout.width) layout.width = b.layout.width;
  if (a.layout.corners !== b.layout.corners) layout.corners = b.layout.corners;
  if (a.layout.photos !== b.layout.photos) layout.photos = b.layout.photos;
  if (Object.keys(layout).length) patch.layout = layout;
  const imagery: Record<string, any> = {};
  if (a.logoUrl !== b.logoUrl || a.logoAlt !== b.logoAlt) {
    imagery.logo = b.logoUrl ? { url: b.logoUrl, alt: b.logoAlt } : null;
  }
  if (a.coverUrl !== b.coverUrl || a.coverAlt !== b.coverAlt) {
    imagery.cover = b.coverUrl ? { url: b.coverUrl, alt: b.coverAlt } : null;
  }
  if (a.iconUrl !== b.iconUrl) imagery.icon = b.iconUrl ? { url: b.iconUrl } : null;
  if (Object.keys(imagery).length) patch.imagery = imagery;
  return patch;
}

const labelOf = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type Vis = 'public' | 'password' | 'offline';

function visOf(
  account: { publicEnabled?: boolean; publicAccessProtected?: boolean } | null | undefined
): Vis {
  if (!account?.publicEnabled) return 'offline';
  if (account.publicAccessProtected) return 'password';
  return 'public';
}

export const CmsSite: React.FC<{ mode?: 'design' | 'settings' }> = ({ mode = 'design' }) => {
  const design = mode === 'design';
  const settings = mode === 'settings';
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: publicData, refetch: refetchPublic } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: articlesData } = useQuery(GET_TINYCMS_ARTICLES, {
    variables: { accountId, state: 'published' },
    skip: !accountId,
  });
  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [loading]);
  const [updateSite, { loading: saving }] = useMutation(UPDATE_TINYCMS_SITE);
  // Templates (UX review v10): switching keeps every page and article.
  const { data: startersData } = useQuery<{ tinycmsStarters: Starter[] }>(TINYCMS_STARTERS, {
    skip: !design,
  });
  const [applyStarter, { loading: applyingTemplate }] = useMutation(APPLY_TINYCMS_STARTER);
  const { data: liveLookData, refetch: refetchLiveLook } = useQuery<{
    tinycmsLiveLook: LiveLook | null;
  }>(TINYCMS_LIVE_LOOK, { variables: { accountId }, skip: !design || !accountId });
  const [revertTheme, { loading: reverting }] = useMutation(REVERT_TINYCMS_SITE_THEME);
  // "What changed": the saved working copy against the live version, loaded
  // when the bar's toggle opens and again whenever the working copy changes.
  const [showChanges, setShowChanges] = useState(false);
  const {
    data: changesData,
    loading: changesLoading,
    refetch: refetchChanges,
  } = useQuery<{ tinycmsSiteChanges: { changes: SiteChange[] } }>(TINYCMS_SITE_CHANGES, {
    variables: { accountId },
    skip: !design || !accountId || !showChanges,
    // Always fresh: a stale list from before a save or publish would mislead.
    fetchPolicy: 'network-only',
  });
  const [discardChanges, { loading: discarding }] = useMutation(DISCARD_TINYCMS_SITE_CHANGES);
  useTemplateFonts();
  // Search engines below reads visibility and the switch: ask again after either.
  const [updatePublic] = useMutation(UPDATE_PUBLIC_SITE, { refetchQueries: [GET_TINYCMS_SEARCH] });
  const [publishContent, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  // The site's menus or home page point at pages that aren't live yet (a new
  // site from a template): Today bundles them, so the site and those pages go
  // live with one yes instead of a dead end (backlog bfbfa5d7).
  const today = useToday('day', { skip: !accountId });
  const { publishTogether, step: togetherStep } = usePublishTogether();
  const [createPreview, { loading: previewing }] = useMutation(CREATE_TINYCMS_PREVIEW);

  const site = data?.tinycmsSite;
  const account = publicData?.account;
  const loaded = useMemo(() => toForm(site?.resolved), [site]);
  const [form, setForm] = useState<SiteForm>(loaded);
  const [title, setTitle] = useState('');
  const [articleBase, setArticleBase] = useState('');
  const [tagline, setTagline] = useState('');
  const [password, setPassword] = useState('');
  const [askPassword, setAskPassword] = useState(false);
  const [override, setOverride] = useState('');
  const [perms, setPerms] = useState<Record<string, number>>({});
  // Surface → when each part last changed, and whether a change waits (Prototype v15).
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId || mode !== 'design',
    fetchPolicy: 'cache-first',
  });
  // Look and pages' confirm names the forms it adds that the site doesn't have.
  const { data: formsData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId || mode !== 'design',
    fetchPolicy: 'cache-first',
  });
  const { data: siteApprovals } = useQuery(GET_TINYCMS_APPROVALS, {
    variables: { accountId, kind: 'site', limit: 1 },
    skip: !accountId || mode !== 'design',
    fetchPolicy: 'cache-and-network',
  });
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => setForm(loaded), [loaded]);
  // The site's name and tagline live in the site document (they publish with
  // the theme, like everything readers see); the account's Public Site values
  // are the fallback until one is set.
  const shownTitle = site?.working?.identity?.title ?? account?.publicTitle ?? '';
  const shownTagline = site?.working?.identity?.tagline ?? account?.publicTagline ?? '';
  useEffect(() => {
    setTitle(shownTitle);
    setTagline(shownTagline);
  }, [shownTitle, shownTagline]);
  // Where articles live (seo.articlesPath): "" is the default, /article/… and /articles.
  const shownArticleBase: string = site?.working?.seo?.articlesPath || '';
  useEffect(() => setArticleBase(shownArticleBase), [shownArticleBase]);

  const patch = diff(loaded, form);
  const dirty = Object.keys(patch).length > 0;
  const unpublished = Boolean(site?.state?.hasUnpublishedChanges);
  const liveVersion = site?.state?.live?.version || 0;
  const workingHash = site?.workingHash;
  // Today's bundle (what goes live with the site, and the checks after it) is
  // asked again for every saved working copy; until it answers for this one,
  // the bar lists no checks rather than stale ones (a template's pages just
  // added would still read "Home isn't live").
  const [bundleFor, setBundleFor] = useState<string | null>(null);
  useEffect(() => {
    if (!accountId || !workingHash) return;
    let gone = false;
    today
      .refetch()
      .catch(() => undefined)
      .then(() => !gone && setBundleFor(workingHash));
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, workingHash]);
  useEffect(() => {
    if (showChanges && accountId) void refetchChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingHash]);
  useEffect(() => {
    if (!unpublished) setShowChanges(false);
  }, [unpublished]);
  const findings: Finding[] = site?.state?.findings || [];
  const siteGroup = today.data?.groups?.find((g) => g.site);
  // Offered while the site's home page or menus point at pages it goes live
  // with (a menu link to one only warns now, but they still go together).
  const together: BundleItem[] | null =
    siteGroup && siteGroup.pages.length + siteGroup.forms.length > 0
      ? bundleItems(siteGroup, today.data?.waiting || [])
      : null;
  const togetherPages = (together || []).filter((i) => i.kind === 'page');
  // Pages that exist but won't be live after this publish: a link to one says
  // "not live yet", not "a page you don't have".
  const togetherIds = new Set(togetherPages.map((p) => p.id));
  const draftPaths = new Set<string>(
    (pagesData?.tinycmsPages || [])
      .filter(
        (p: { id: string; state?: { live?: unknown } }) => !p.state?.live && !togetherIds.has(p.id)
      )
      .map((p: { path: string }) => p.path.replace(/^\/+/, ''))
  );
  // Custom CSS gets its own Checks section, in plain words (CssChecks).
  const cssFindings = findings.filter((f) => f.code === 'css_hooks');

  const savedVis = visOf(account);
  const vis: Vis = askPassword ? 'password' : savedVis;
  // The server knows the real address (custom domain, platform host or free address).
  const host = site?.siteUrl ? String(site.siteUrl).replace(/^https?:\/\//, '') : null;

  // The real site in the Design header, not a drawing of the tokens: one
  // preview token per visit gives both addresses — the working copy
  // (?preview=) and the live site at the same origin.
  const [frames, setFrames] = useState<{ working: string; live: string } | null>(null);
  // "Browse full previews" (Prototype v15): every template, page by page, in a dialog.
  const [browsing, setBrowsing] = useState(false);
  useEffect(() => {
    if (!browsing) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setBrowsing(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [browsing]);
  useEffect(() => {
    if (!design || !host || !accountId || frames) return;
    let gone = false;
    createPreview({ variables: { accountId } })
      .then(({ data: made }) => {
        const url = made?.createTinycmsPreview?.url;
        if (!gone && url) setFrames(previewAddresses(url));
      })
      .catch(() => {
        /* no handle yet, or offline: the header falls back to the drawn theme */
      });
    return () => {
      gone = true;
    };
  }, [design, host, accountId, frames, createPreview]);
  const siteTitle = title.trim() || account?.name || 'Site';
  const titles: string[] = (articlesData?.tinycmsArticles || [])
    .map((a: { title?: string }) => decode(a.title || ''))
    .filter(Boolean)
    .slice(0, 3);

  const saveWorking = async () => {
    if (!dirty) return site?.workingHash as string;
    for (const key of COLOR_KEYS) {
      if (form.colors[key] && !HEX.test(form.colors[key])) {
        throw new Error(`${COLOR_LABELS[key]} needs a color like #1a2b3c.`);
      }
    }
    await updateSite({ variables: { accountId, input: patch } });
    const fresh = await refetch();
    return fresh.data?.tinycmsSite?.workingHash as string;
  };

  const publishTheme = async () => {
    setMessage(null);
    try {
      const hash = await saveWorking();
      const { data: published } = await publishContent({
        variables: {
          accountId,
          kind: 'site',
          action: 'publish',
          contentHash: hash,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = published?.publishTinycmsContent;
      if (r?.ok) {
        setOverride('');
        // The live look is what "Your own look" shows; it just changed.
        void refetchLiveLook();
        setMessage({
          tone: 'ok',
          text: r.unchanged ? 'Already live.' : `Published theme v${r.version}.`,
        });
      } else {
        setMessage({ tone: 'error', text: r?.message || 'Publish was refused.' });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** The site with the pages (and their forms) its links need, as one approval. */
  const publishWithPages = async () => {
    setMessage(null);
    try {
      const hash = await saveWorking();
      // Today's bundle again, for the version just saved.
      const [fresh] = await today.refetch();
      const data = fresh?.data?.tinycmsToday;
      const group = data?.groups?.find((g) => g.site);
      if (!group) {
        await publishTheme();
        return;
      }
      const items = bundleItems(group, data.waiting).map((i) =>
        i.kind === 'site' ? { ...i, workingHash: hash } : i
      );
      const r = await publishTogether(items);
      if (r.ok) {
        const n = items.filter((i) => i.kind === 'page').length;
        setOverride('');
        void refetchLiveLook();
        setMessage({
          tone: 'ok',
          text: `Published the site with ${n} page${n === 1 ? '' : 's'}. It’s live now.`,
        });
      } else {
        setMessage({ tone: 'error', text: r.message });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** Everything changed since the live version goes; readers see nothing different. */
  /** Pages and forms a template's Look and pages added since the last publish. */
  const pendingTemplateAdded = (): TemplateAdded | null =>
    site?.working?.starter?.appliedAt !== liveLook?.starter?.appliedAt
      ? site?.working?.starter?.added || null
      : null;

  const discardWorking = async () => {
    // They go with it, unless edited (backlog 70a50c12).
    const added = pendingTemplateAdded();
    const ok = await askInk({
      title: 'Discard the working changes?',
      body: `Readers keep seeing ${liveVersion ? `theme v${liveVersion}` : 'the default theme'}. Everything changed since then goes${
        added || currentTemplate !== (liveLook?.starter?.id || null)
          ? ': the template, colours, fonts, and any draft pages, menu and footer added with it.'
          : '.'
      }`,
      confirmLabel: 'Discard',
      danger: true,
    });
    if (!ok) return;
    setMessage(null);
    try {
      const { data: done } = await discardChanges({ variables: { accountId } });
      const drafts = done?.discardTinycmsSiteChanges?.templateDrafts;
      showInkToast(discardResultText(liveVersion, drafts));
      // Drafts it took back left Pages and Forms (and Today's count).
      if (drafts?.removed.length) void today.refetch();
      // The refetched site recomputes `loaded`, and the effect on it resets the form.
      await Promise.all([refetch(), refetchLiveLook()]);
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** A published version back on the working copy (History → Restore this version). */
  const afterRestore = async () => {
    setMessage(null);
    await Promise.all([refetch(), refetchLiveLook()]);
  };

  const preview = async () => {
    setMessage(null);
    const win = window.open('', '_blank');
    try {
      await saveWorking();
      const { data: created } = await createPreview({ variables: { accountId } });
      const url = created?.createTinycmsPreview?.url;
      if (win && url) win.location.href = url;
      else win?.close();
    } catch (err: any) {
      win?.close();
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const savePublic = async (input: Record<string, unknown>) => {
    setMessage(null);
    try {
      await updatePublic({ variables: { accountId, input } });
      await refetchPublic();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const chooseVis = async (next: Vis) => {
    if (next === 'password') {
      setAskPassword(true);
      return;
    }
    setAskPassword(false);
    if (next === 'offline') {
      await savePublic({ publicEnabled: false });
      return;
    }
    if (account?.publicAccessProtected) {
      if (
        !(await askInk({
          title: 'Remove the password?',
          body: 'Anyone with the address will be able to see this site.',
          confirmLabel: 'Remove password',
          danger: true,
        }))
      )
        return;
      await savePublic({ publicEnabled: true, publicAccessPassword: '' });
      return;
    }
    await savePublic({ publicEnabled: true });
  };

  /** Search engines: Allow lists the site; Block serves every page noindex, immediately. */
  const chooseSearch = async (next: 'allow' | 'block') => {
    const blocked = next === 'block';
    if (blocked === Boolean(account?.publicSearchBlocked)) return;
    if (
      blocked &&
      !(await askInk({
        title: 'Keep this site out of search engines?',
        body: 'Every page will ask Google and the other engines not to index it, and the sitemap goes away. Pages already listed drop out over the following weeks. You can allow them again any time.',
        confirmLabel: 'Block search engines',
        danger: true,
      }))
    )
      return;
    await savePublic({ publicSearchBlocked: blocked });
  };

  /** AI assistants: which of them robots.txt asks to stay out. Saved at once, like Search engines. */
  const chooseAi = async (next: 'allow' | 'answers' | 'block') => {
    if (next === (account?.publicAiCrawlers || 'allow')) return;
    if (
      next === 'block' &&
      !(await askInk({
        title: 'Ask AI assistants not to read this site?',
        body: 'People asking AI assistants about what you do may stop seeing your pages in the answers. You can allow them again any time.',
        confirmLabel: 'Block AI assistants',
        danger: true,
      }))
    )
      return;
    await savePublic({ publicAiCrawlers: next });
  };

  const savePassword = async () => {
    if (!password.trim()) {
      setMessage({ tone: 'error', text: 'Enter a password to protect the site.' });
      return;
    }
    await savePublic({ publicEnabled: true, publicAccessPassword: password });
    setPassword('');
    setAskPassword(false);
  };

  const saveIdentity = async () => {
    const nextTitle = title.trim();
    const nextTagline = tagline.trim();
    if (nextTitle === shownTitle && nextTagline === shownTagline) return;
    setMessage(null);
    try {
      // Onto the working copy: readers see it when the theme is next published.
      await updateSite({
        variables: {
          accountId,
          input: { identity: { title: nextTitle || null, tagline: nextTagline || null } },
        },
      });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const saveArticleBase = async () => {
    const next = articleBase.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    if (next === shownArticleBase || (next === 'article' && !shownArticleBase)) {
      setArticleBase(shownArticleBase);
      return;
    }
    setMessage(null);
    try {
      // Onto the working copy: the addresses change when the theme is next published.
      await updateSite({
        variables: { accountId, input: { seo: { articlesPath: next || null } } },
      });
      await refetch();
    } catch (err: any) {
      setArticleBase(shownArticleBase);
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const currentTemplate: string | null = site?.working?.starter?.id || null;

  /** Switch the working theme to a template's tokens; pages and articles stay. */
  const chooseTemplate = async (id: string) => {
    setMessage(null);
    try {
      await applyStarter({ variables: { accountId, starterId: id, only: 'look' } });
      await refetch();
      const name = startersData?.tinycmsStarters.find((t) => t.id === id)?.name;
      if (name) showInkToast(lookText(name));
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /**
   * The template's look AND its pages: its sample pages, forms, navigation
   * and home page as unpublished working copies, in the site's name. Pages
   * that already exist at an address are kept as they are.
   */
  const chooseTemplatePages = async (t: Starter) => {
    setMessage(null);
    // Design review round 27 §2: what it brings, row by row, before anything is added.
    const ok = await askInk({
      ...lookPagesConfirm({
        template: t.name,
        siteName: siteTitle,
        pages: t.pages,
        menu: t.menu || [],
        forms: t.forms || [],
        havePaths: new Set(
          (pagesData?.tinycmsPages || []).map((p: { path: string }) => p.path.replace(/^\/+/, ''))
        ),
        haveForms: new Set((formsData?.tinycmsForms || []).map((f: { name: string }) => f.name)),
        currentMenu: (site?.resolved?.navigation?.header || [])
          .map((l: { label?: string }) => l.label)
          .filter(Boolean),
        contactEmail: site?.working?.business?.email,
      }),
      confirmLabel: 'Add pages',
    });
    if (!ok) return;
    try {
      const r = await applyStarter({ variables: { accountId, starterId: t.id, only: 'all' } });
      await refetch();
      // Every page and form it added or kept, by name (backlogs dd3fa9f5, 70a50c12).
      showInkToast(lookPagesText(t.name, r.data?.applyTinycmsStarter || {}));
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** Back to the live look (and its template record, if it had one). */
  const revertLook = async () => {
    setMessage(null);
    try {
      await revertTheme({ variables: { accountId } });
      await Promise.all([refetch(), refetchLiveLook()]);
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const liveLook = liveLookData?.tinycmsLiveLook ?? null;
  // Custom CSS (set by the agent over MCP) sits on top of whatever template is
  // chosen: a template replaces the tokens underneath it, not the CSS.
  const customCss: string =
    typeof site?.working?.customCss === 'string' ? site.working.customCss : '';
  const customCssLines = customCss.trim() ? customCss.trim().split('\n').length : 0;
  // The switch: off keeps the CSS on the working copy but the site renders without it.
  const customCssOn = site?.working?.customCssEnabled !== false;
  const toggleCustomCss = async () => {
    setMessage(null);
    try {
      await updateSite({ variables: { accountId, input: { customCssEnabled: !customCssOn } } });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };
  // "Your own look" is offered while the live site is not a template: the
  // way back for a site styled by hand (every site from before templates).
  const ownLookOffered = !liveLook?.starter;
  const ownLookCurrent = !currentTemplate;

  const resetPreset = async () => {
    setMessage(null);
    try {
      if (currentTemplate) {
        await applyStarter({ variables: { accountId, starterId: currentTemplate, only: 'look' } });
      } else {
        await updateSite({
          variables: {
            accountId,
            input: {
              colors: Object.fromEntries(COLOR_KEYS.map((k) => [k, null])),
              fonts: { heading: null, body: null },
              layout: { home: null, width: null, corners: null, photos: null },
            },
          },
        });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  if (loading && !site) {
    return (
      <div className="ink-body">
        <Loader style={{ color: 'var(--ink-ink3)' }} />
      </div>
    );
  }
  if (!site) {
    return (
      <div className="ink-body">
        <p style={{ color: 'var(--ink-ink3)' }}>The site settings are unavailable.</p>
      </div>
    );
  }

  const visMeta: Record<Vis, { label: string; dot: string }> = {
    public: { label: 'Live · Public', dot: 'var(--ink-green)' },
    password: { label: 'Live · Password protected', dot: 'var(--ink-amber)' },
    offline: { label: 'Offline', dot: 'var(--ink-ink3)' },
  };
  const showBar = dirty || unpublished;
  // Everything that matters for Publish, listed under it, as the site will be
  // after this publish (backlog c4b822db): pages going live with it count as
  // live. Until Today's bundle loads, nothing is said rather than something wrong.
  const workingTemplate =
    startersData?.tinycmsStarters.find((t) => t.id === currentTemplate) || null;
  // What Changed (round 27 §4): the server's changes, plus the pages and forms
  // a template added since the last publish.
  const changeGroups = whatChanged(changesData?.tinycmsSiteChanges?.changes || [], {
    added: pendingTemplateAdded(),
    templateNames: Object.fromEntries(
      (startersData?.tinycmsStarters || []).map((t) => [t.id, t.name])
    ),
  });
  const checkFindings: Finding[] = (together && siteGroup?.siteFindings) || findings;
  const checks = publishChecks({
    findings: checkFindings,
    goesLive: (together || [])
      .filter((i) => i.kind !== 'site')
      .map((i) => ({ kind: i.kind as 'page' | 'form', title: i.title })),
    otherChecks: together ? siteGroup?.otherChecks || null : null,
    template: workingTemplate,
    draftPaths,
  });
  const checksReady = bundleFor === workingHash;
  // A typed override reason still lets the site alone go out over a finding,
  // but never onto a front page that isn't there.
  const overridable = !together && !checks.rows.some((r) => r.key === 'home');
  const held = checksReady && Boolean(checks.blockedTip) && !(overridable && override.trim());
  const heldClick = () =>
    showInkToast(`${checks.blockedTip}. It’s listed under the Publish button.`);
  const menuTo = `/content?view=pages&page=${checks.menuPart}`;
  const runCheck = async (id: PublishAction) => {
    if (id === 'addHome' || id === 'addPages') {
      if (!workingTemplate) return;
      await chooseTemplatePages(workingTemplate);
      return;
    }
    if (id === 'keepSections') {
      const gone = checkFindings.find((f) => f.code === 'section_removed') as
        | (Finding & { restore?: Array<{ slug: string }> })
        | undefined;
      if (!gone?.restore?.length) return;
      setMessage(null);
      try {
        const have: Array<{ slug: string }> = site?.working?.collections || [];
        const back = gone.restore.filter((c) => !have.some((h) => h.slug === c.slug));
        await updateSite({ variables: { accountId, input: { collections: [...have, ...back] } } });
        await refetch();
      } catch (err: any) {
        setMessage({ tone: 'error', text: err.message });
      }
      return;
    }
    if (id === 'keepArticles') {
      setMessage(null);
      try {
        // The article list becomes the front page, on the working copy.
        await updateSite({ variables: { accountId, input: { layout: { homePage: null } } } });
        await refetch();
      } catch (err: any) {
        setMessage({ tone: 'error', text: err.message });
      }
    }
  };
  const nextVersion = liveVersion + 1;
  const list = form.layout.home === 'list';
  const maxw =
    form.layout.width === 'narrow' ? '62%' : form.layout.width === 'standard' ? '80%' : '100%';
  const previewItems = titles.length
    ? titles
    : ['Your latest article', 'Another story', 'One more'];

  // What each Surface row says changed last: a waiting change first (it needs
  // you), else who published it and when. Topic hubs and public fields keep no
  // such record, so they say nothing rather than guess.
  const homePath = site?.working?.layout?.homePage ? `/${site.working.layout.homePage}` : null;
  const homePage = homePath
    ? (pagesData?.tinycmsPages || []).find((p: { path: string }) => p.path === homePath)
    : null;
  const lastSite = (siteApprovals?.tinycmsApprovals || []).find(
    (a: { outcome: string }) => a.outcome === 'accepted'
  );
  const surfaceChange: Record<string, { text: string; to?: string; waiting?: boolean } | null> = {
    home:
      homePage && homePage.state?.hasUnpublishedChanges && !homePage.state?.scheduled
        ? {
            text: 'Change waiting for approval →',
            to: `/content?view=pages&page=${homePage.id}`,
            waiting: true,
          }
        : homePage?.state?.lastApproval?.by
          ? {
              text: `${homePage.state.lastApproval.by} · ${changedWhen(homePage.state.lastApproval.at)}`,
            }
          : null,
    nav: unpublished
      ? { text: 'Change waiting for approval', waiting: true }
      : lastSite
        ? { text: `${approverPhrase(lastSite)} · ${changedWhen(lastSite.recordedAt)}` }
        : null,
    hubs: null,
    public: null,
  };
  surfaceChange.theme = surfaceChange.nav;

  // The theme drawn from its tokens: the header before a preview address
  // exists, and what shows while the real site loads in the frame (QA round 7:
  // a blank grey box for 8–10 s).
  const drawnTheme = (withBadge: boolean) => (
    <div
      className="ink-site-preview"
      style={{
        background: form.colors.background || '#fff',
        color: form.colors.text || '#111',
      }}
    >
      {withBadge ? (
        <span className="ink-site-badge">
          {dirty || unpublished ? 'Working theme' : 'Live theme'}
        </span>
      ) : null}
      <div className="ink-site-preview-head">
        <span style={{ font: `700 17px/1.2 ${fontStack(form.fonts.heading, 'serif')}` }}>
          {siteTitle}
        </span>
        {tagline ? (
          <span
            style={{
              font: `400 12px/1.3 ${fontStack(form.fonts.body, 'sans')}`,
              color: form.colors.muted,
            }}
          >
            {tagline}
          </span>
        ) : null}
      </div>
      <div className="ink-site-preview-body" style={{ maxWidth: maxw }}>
        {list ? (
          previewItems.map((item) => (
            <div
              key={item}
              style={{
                padding: '8px 0',
                borderBottom: '1px solid color-mix(in srgb, currentColor 14%, transparent)',
              }}
            >
              <div style={{ font: `600 14px/1.3 ${fontStack(form.fonts.heading, 'serif')}` }}>
                {item}
              </div>
              <div
                style={{
                  font: `400 11.5px/1.3 ${fontStack(form.fonts.body, 'sans')}`,
                  color: form.colors.muted,
                  marginTop: 2,
                }}
              >
                <span style={{ color: form.colors.accent }}>Read →</span>
              </div>
            </div>
          ))
        ) : (
          <div className="ink-site-cards">
            {previewItems.map((item) => (
              <div key={item}>
                <div className="ink-site-card-thumb" />
                <div
                  style={{
                    font: `600 12px/1.3 ${fontStack(form.fonts.heading, 'serif')}`,
                    marginTop: 6,
                  }}
                >
                  {item}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={design ? 'ink-site-embedded' : 'ink-body'}>
      {showBar ? (
        <div className="ink-site-bar">
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, background: 'var(--ink-amber)' }}
          />
          <span style={{ font: '500 14px var(--ink-font)' }}>
            {workingTemplate
              ? `Working theme: ${workingTemplate.name}.`
              : 'Theme changes are on the working copy.'}
          </span>
          <span className="ink-site-bar-note">
            {liveVersion
              ? `Readers still see theme v${liveVersion}.`
              : 'Readers still see the default theme.'}
          </span>
          <span className="ink-site-bar-actions">
            {unpublished ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => setShowChanges((v) => !v)}
                aria-expanded={showChanges}
              >
                {showChanges ? 'Hide changes' : 'What changed'}
              </button>
            ) : null}
            {unpublished ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => void discardWorking()}
                disabled={discarding || saving || publishing}
              >
                {discarding ? 'Discarding…' : 'Discard changes'}
              </button>
            ) : dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => setForm(loaded)}
              >
                Discard
              </button>
            ) : null}
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              onClick={() => void preview()}
              disabled={previewing || saving}
            >
              Preview ↗
            </button>
            {together ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => (held ? heldClick() : void publishWithPages())}
                disabled={Boolean(togetherStep) || saving}
                aria-disabled={held}
                data-held={held || undefined}
                title={held ? checks.blockedTip || undefined : undefined}
              >
                {togetherStep === 'checking'
                  ? 'Checking the wording…'
                  : togetherStep || saving
                    ? 'Publishing…'
                    : `Publish with ${togetherPages.length} page${togetherPages.length === 1 ? '' : 's'}`}
              </button>
            ) : (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => (held ? heldClick() : void publishTheme())}
                disabled={publishing || saving}
                aria-disabled={held}
                data-held={held || undefined}
                title={held ? checks.blockedTip || undefined : undefined}
              >
                {publishing || saving ? 'Publishing…' : `Publish theme v${nextVersion}`}
              </button>
            )}
          </span>
          {checksReady && checks.rows.length ? (
            <div className="ink-site-bar-checks">
              <div className="ink-site-bar-checks-head">{checks.head}</div>
              {checks.rows.map((row) => (
                <div key={row.key} className="ink-site-bar-check">
                  <span className="ink-site-bar-tag" data-kind={row.kind}>
                    {tagOf(row.kind)}
                  </span>
                  <span className="ink-site-bar-check-text">
                    {row.text}
                    {row.sub ? <span> {row.sub}</span> : null}
                  </span>
                  <span className="ink-site-bar-check-acts">
                    {row.actions.map((a) =>
                      a.id === 'editMenu' || a.id === 'reviewPages' ? (
                        <Link key={a.id} to={a.id === 'editMenu' ? menuTo : '/content?view=pages'}>
                          {a.label}
                        </Link>
                      ) : (
                        <button
                          key={a.id}
                          type="button"
                          className="ink-link-btn"
                          onClick={() => void runCheck(a.id)}
                          disabled={saving || applyingTemplate}
                        >
                          {a.label}
                        </button>
                      )
                    )}
                  </span>
                </div>
              ))}
              {checks.blockedTip && overridable ? (
                <input
                  className="ink-site-input"
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  placeholder="Or publish anyway: give a reason"
                  aria-label="Override reason"
                />
              ) : null}
            </div>
          ) : null}
          {showChanges && unpublished ? (
            changesLoading || !changeGroups.length ? (
              <div className="ink-site-bar-changes">
                <p className="ink-changes-empty">
                  {changesLoading
                    ? 'Comparing…'
                    : 'The saved working copy is the same as what readers see.'}
                </p>
              </div>
            ) : (
              <>
                <WhatChanged
                  groups={changeGroups}
                  since={liveVersion ? `theme v${liveVersion}` : 'the default theme'}
                />
                {dirty ? (
                  <p className="ink-changes-empty" style={{ paddingLeft: 20 }}>
                    Edits you haven’t saved yet aren’t listed; Preview or Publish saves them.
                  </p>
                ) : null}
              </>
            )
          ) : null}
        </div>
      ) : null}

      {design ? (
        <div className="ink-site-hero">
          <div>
            <div className="ink-site-status">
              <span
                className="ink-dot"
                style={{ width: 8, height: 8, background: visMeta[vis].dot }}
              />
              {visMeta[vis].label}
            </div>
            <h1 className="ink-site-name">{siteTitle}</h1>
            {host ? (
              <a className="ink-site-url" href={`https://${host}`} target="_blank" rel="noreferrer">
                {host} ↗
              </a>
            ) : (
              <div className="ink-site-url" style={{ color: 'var(--ink-ink3)' }}>
                Choose a handle to give the site an address.
              </div>
            )}
          </div>
          {frames ? (
            <SitePreviewFrame
              src={unpublished ? frames.working : frames.live}
              height={320}
              eager
              placeholder={drawnTheme(false)}
              title={unpublished ? 'The working copy of your site' : 'Your site as readers see it'}
              reloadKey={`${site?.workingHash || ''}:${unpublished ? 'working' : 'live'}`}
            >
              <span className="ink-site-badge">
                {dirty || unpublished ? 'Working copy' : 'Live'}
              </span>
              <a
                className="ink-site-frame-open"
                href={unpublished ? frames.working : frames.live}
                target="_blank"
                rel="noreferrer"
              >
                Open ↗
              </a>
            </SitePreviewFrame>
          ) : (
            drawnTheme(true)
          )}
        </div>
      ) : null}

      {message ? (
        <p
          style={{
            margin: '16px 0 0',
            font: '400 14px var(--ink-font)',
            color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)',
          }}
        >
          {message.text}
        </p>
      ) : null}

      {settings ? (
        <InkSection
          id="visibility"
          title="Visibility"
          description="Changes take effect immediately. Nothing stays cached."
        >
          <div className="ink-site-choices">
            {(
              [
                [
                  'public',
                  'Public',
                  account?.publicSearchBlocked
                    ? 'Anyone with the link.'
                    : 'Anyone with the link, and search engines.',
                ],
                ['password', 'Password', 'Private while you build it out.'],
                ['offline', 'Offline', 'Content stays here. The site is down.'],
              ] as const
            ).map(([key, label, desc]) => (
              <button
                key={key}
                type="button"
                className="ink-site-choice"
                data-on={vis === key}
                onClick={() => void chooseVis(key)}
              >
                <span className="ink-site-choice-title">
                  {label}
                  <span className="ink-site-radio" />
                </span>
                <span className="ink-site-choice-desc">{desc}</span>
              </button>
            ))}
          </div>
          {vis === 'password' ? (
            <div className="ink-site-pass">
              <PasswordInput
                className="ink-site-input"
                value={password}
                {...NOT_A_LOGIN}
                placeholder="Password"
                aria-label="Site password"
                aria-describedby="site-password-help"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => void savePassword()}
              >
                Save password
              </button>
              <span id="site-password-help" className="ink-form-hint" style={{ flexBasis: '100%' }}>
                {account?.publicAccessProtected
                  ? 'Leave blank to keep the current password'
                  : 'Visitors enter it before they see anything'}
              </span>
            </div>
          ) : null}
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="identity"
          title="Identity"
          description="How the site introduces itself in browsers, search results and link previews. Publishes with the theme."
        >
          <div className="ink-site-fields">
            <label className="ink-site-label">
              Site title
              <input
                className="ink-site-input"
                value={title}
                placeholder="Site title"
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => void saveIdentity()}
              />
            </label>
            <label className="ink-site-label">
              Tagline
              <input
                className="ink-site-input"
                value={tagline}
                placeholder="Tagline"
                onChange={(e) => setTagline(e.target.value)}
                onBlur={() => void saveIdentity()}
              />
            </label>
            <div className="ink-site-label">
              Logo
              <div className="ink-site-media">
                {form.logoUrl ? (
                  <img className="ink-site-logo" src={form.logoUrl} alt="" />
                ) : (
                  <span className="ink-site-logo" />
                )}
                <label className="ink-site-alt">
                  Alt text
                  <input
                    className="ink-site-input"
                    value={form.logoAlt}
                    placeholder="Alt text"
                    onChange={(e) => setForm({ ...form, logoAlt: e.target.value })}
                  />
                </label>
              </div>
              <input
                className="ink-site-input"
                value={form.logoUrl}
                placeholder="https://example.com/logo.png"
                aria-label="Logo image address"
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              />
            </div>
            <div className="ink-site-label">
              Home cover image
              <div className="ink-site-media">
                {form.coverUrl ? (
                  <img className="ink-site-cover" src={form.coverUrl} alt="" />
                ) : (
                  <span className="ink-site-cover" />
                )}
                <label className="ink-site-alt">
                  Alt text · required with an image
                  <input
                    className="ink-site-input"
                    value={form.coverAlt}
                    placeholder="Alt text"
                    onChange={(e) => setForm({ ...form, coverAlt: e.target.value })}
                  />
                </label>
              </div>
              <input
                className="ink-site-input"
                value={form.coverUrl}
                placeholder="https://example.com/cover.jpg"
                aria-label="Cover image address"
                onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
              />
            </div>
            <div className="ink-site-label">
              Site icon
              <div className="ink-site-media">
                {form.iconUrl ? (
                  <img className="ink-site-icon" src={form.iconUrl} alt="" />
                ) : (
                  <span className="ink-site-icon" />
                )}
                <span className="ink-site-row-desc">
                  Shown in browser tabs and on phone home screens. A square PNG, at least 180×180.
                </span>
              </div>
              <input
                className="ink-site-input"
                value={form.iconUrl}
                placeholder="https://example.com/icon.png"
                aria-label="Site icon image address"
                onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
              />
            </div>
          </div>
        </InkSection>
      ) : null}

      {design ? (
        <InkSection
          id="theme"
          title="Template"
          aside={
            <button
              type="button"
              className="ink-section-aside-link"
              onClick={() => setBrowsing(true)}
            >
              Browse full previews ↗
            </button>
          }
          description="The look of your public site. Each picture is the template’s own front page in your words and with your cover photo. Use the look keeps every page and article and changes colours, type, shapes and the header; Look and pages also adds the template’s pages as drafts. Changes stay on the working theme until you publish."
        >
          <div className="ink-site-stack">
            {customCssLines ? (
              <div className="ink-tpl-css-note" role="note" data-on={customCssOn}>
                <p>
                  {customCssOn ? (
                    <>
                      <strong>This site has its own CSS on top of the theme.</strong>{' '}
                      {customCssLines === 1 ? 'One line' : `${customCssLines} lines`} of custom CSS,
                      set by your agent, restyle parts of the site. A template changes the colours,
                      fonts and shapes underneath that CSS, so some of the template may not show.
                      Turn the CSS off to see a template on its own; it is kept, and comes back when
                      you turn it on.
                    </>
                  ) : (
                    <>
                      <strong>Custom CSS is off on the working copy.</strong> The site renders from
                      the theme alone; the{' '}
                      {customCssLines === 1 ? 'line' : `${customCssLines} lines`} of CSS are kept.
                      Publish to make this live, or turn it back on.
                    </>
                  )}
                </p>
                <button
                  type="button"
                  className="ink-tpl-css-switch"
                  role="switch"
                  aria-checked={customCssOn}
                  disabled={saving}
                  onClick={() => void toggleCustomCss()}
                >
                  <span>Custom CSS {customCssOn ? 'on' : 'off'}</span>
                  <span className="ink-switch" data-on={customCssOn} />
                </button>
              </div>
            ) : null}
            <div className="ink-tpl-grid">
              {ownLookOffered ? (
                <button
                  type="button"
                  className="ink-tpl-card ink-tpl-own"
                  data-on={ownLookCurrent}
                  disabled={applyingTemplate || reverting || ownLookCurrent}
                  onClick={() => void revertLook()}
                >
                  <span className="ink-tpl-thumb">
                    {frames && liveLook ? (
                      <SitePreviewFrame
                        src={frames.live}
                        height={190}
                        title="Your site as readers see it"
                      />
                    ) : (
                      <OwnLookThumb form={form} siteTitle={siteTitle} tagline={tagline} />
                    )}
                    {ownLookCurrent ? (
                      <span className="ink-tpl-badge" data-tone={unpublished ? 'working' : 'live'}>
                        {unpublished ? 'Working copy' : 'Live'}
                      </span>
                    ) : null}
                  </span>
                  <span className="ink-tpl-text">
                    <span className="ink-tpl-name">Your own look</span>
                    <span className="ink-tpl-blurb">
                      {ownLookCurrent
                        ? 'Your colours, fonts and layout, as they are.'
                        : liveLook
                          ? 'Go back to the look readers see today.'
                          : 'Go back to the default look.'}
                    </span>
                  </span>
                </button>
              ) : null}
              {(startersData?.tinycmsStarters || []).map((t) => {
                const isCur = t.id === currentTemplate;
                return (
                  <div key={t.id} className="ink-tpl-card ink-tpl-card-static" data-on={isCur}>
                    <span className="ink-tpl-thumb">
                      <span
                        style={{ zoom: 0.72, height: 190, display: 'block', pointerEvents: 'none' }}
                      >
                        <StarterThumb
                          starter={t}
                          siteName={siteTitle}
                          siteAddr={host?.split('.')[0] || 'yourname'}
                          tagline={shownTagline}
                          titles={titles}
                          coverUrl={form.coverUrl}
                        />
                      </span>
                      {isCur ? (
                        <span
                          className="ink-tpl-badge"
                          data-tone={unpublished ? 'working' : 'live'}
                        >
                          {unpublished ? 'Working copy' : 'Live'}
                        </span>
                      ) : null}
                    </span>
                    <span className="ink-tpl-text">
                      <span className="ink-tpl-name">{t.name}</span>
                      <span className="ink-tpl-blurb">{t.blurb}</span>
                      <span className="ink-tpl-note">
                        The layout in the picture comes with its pages; the look alone changes
                        colours, type, shapes and the header.
                      </span>
                      {/* The template in use says so instead of offering itself (Prototype v15). */}
                      {isCur ? (
                        <span className="ink-tpl-inuse">
                          {liveLook && liveLook.starter?.id !== t.id
                            ? 'On your working theme'
                            : 'In use'}
                        </span>
                      ) : (
                        <span className="ink-tpl-actions">
                          <button
                            type="button"
                            className="ink-btn ink-btn-quiet ink-btn-sm"
                            disabled={applyingTemplate}
                            onClick={() => void chooseTemplate(t.id)}
                          >
                            Use the look
                          </button>
                          <button
                            type="button"
                            className="ink-btn ink-btn-primary ink-btn-sm"
                            disabled={applyingTemplate}
                            onClick={() => void chooseTemplatePages(t)}
                          >
                            Look and pages
                          </button>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="ink-site-kicker">Colours</div>
              <div className="ink-site-swatches">
                {COLOR_KEYS.map((key) => (
                  <label key={key} className="ink-site-swatch">
                    <input
                      type="color"
                      aria-label={COLOR_LABELS[key]}
                      value={HEX.test(form.colors[key] || '') ? form.colors[key] : '#000000'}
                      onChange={(e) =>
                        setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                      }
                    />
                    <span>
                      <span style={{ display: 'block', font: '500 13px var(--ink-font)' }}>
                        {COLOR_LABELS[key]}
                      </span>
                      <input
                        type="text"
                        value={form.colors[key] || ''}
                        placeholder="#1A1A1A"
                        aria-label={`${COLOR_LABELS[key]} hex code`}
                        onChange={(e) =>
                          setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                        }
                      />
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="ink-site-controls">
              <div className="ink-site-control">
                <span style={{ font: '500 13px var(--ink-font)' }}>Fonts</span>
                <span className="ink-tpl-fonts">
                  <span
                    style={{
                      font: `400 22px ${fontStack(form.fonts.heading, 'serif')}`,
                      color: 'var(--ink-ink)',
                    }}
                  >
                    Aa
                  </span>
                  <span>
                    {form.fonts.heading} + {form.fonts.body}
                  </span>
                </span>
              </div>
              {(
                [
                  ['corners', 'Corners', ['square', 'soft', 'round'], form.layout.corners],
                  ['photos', 'Photos', ['colour', 'grey', 'duotone'], form.layout.photos],
                ] as const
              ).map(([key, label, options, value]) => (
                <div key={key} className="ink-site-control">
                  <span style={{ font: '500 13px var(--ink-font)' }}>{label}</span>
                  <span className="ink-seg">
                    {options.map((option: string) => (
                      <button
                        key={option}
                        type="button"
                        data-active={value === option}
                        onClick={() =>
                          setForm({ ...form, layout: { ...form.layout, [key]: option } })
                        }
                      >
                        {option === 'grey' ? 'Greyscale' : labelOf(option)}
                      </button>
                    ))}
                  </span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="ink-site-link"
              onClick={() => void resetPreset()}
              disabled={saving}
            >
              {currentTemplate
                ? 'Reset to the template’s own colours, fonts and photos'
                : 'Reset colours, fonts and layout to the defaults'}
            </button>
          </div>
        </InkSection>
      ) : null}

      {browsing ? (
        <div
          className="ink-modal-back"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setBrowsing(false);
          }}
        >
          <div
            className="ink-modal ink-tpl-browse-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Template previews"
          >
            <div className="ink-tpl-browse-head">
              <div className="ink-modal-title">Template previews</div>
              <button type="button" className="ink-link-btn" onClick={() => setBrowsing(false)}>
                Close
              </button>
            </div>
            <p className="ink-modal-copy">
              Every page each template makes, in {siteTitle}’s name. To use one, close this and pick
              it from the cards.
            </p>
            <StarterStep
              browse
              stepOf=""
              siteName={siteTitle}
              siteAddr={host?.split('.')[0] || 'yourname'}
              onDone={() => setBrowsing(false)}
            />
          </div>
        </div>
      ) : null}

      {design ? (
        <CssChecks
          findings={cssFindings}
          templateName={
            startersData?.tinycmsStarters.find((t) => t.id === currentTemplate)?.name || null
          }
        />
      ) : null}

      {/* Reader questions moved to Prism AI → Settings (backlog 8938bafb). */}
      {design ? (
        <InkSection
          id="reader-questions"
          title="Reader questions"
          description="Visitors ask, and Prism AI answers from what you’ve published."
        >
          <Link to="/prism-ai/settings#reader-questions" className="ink-quiet-link">
            Set them up in Prism AI settings →
          </Link>
        </InkSection>
      ) : null}

      {design ? (
        <InkSection
          id="surface"
          title="Surface"
          description="The parts of the site readers see, and what agents are allowed to change in each. Click a permission to change it."
        >
          <div className="ink-site-card">
            {SURFACES.map((row) => {
              const index = perms[row.key] ?? row.def;
              const perm = PERM_LABELS[index];
              return (
                <div className="ink-site-surface" key={row.key}>
                  <div>
                    <div className="ink-site-surface-title">{row.name}</div>
                    <div className="ink-site-row-desc">{row.desc}</div>
                  </div>
                  {(() => {
                    const ch = surfaceChange[row.key];
                    if (!ch) return <span />;
                    return ch.to ? (
                      <Link
                        to={ch.to}
                        className="ink-site-surface-changed"
                        data-waiting={ch.waiting ? 'true' : undefined}
                      >
                        {ch.text}
                      </Link>
                    ) : (
                      <span
                        className="ink-site-surface-changed"
                        data-waiting={ch.waiting ? 'true' : undefined}
                      >
                        {ch.text}
                      </span>
                    );
                  })()}
                  <button
                    type="button"
                    className="ink-site-perm"
                    style={{ background: perm.bg, color: perm.color }}
                    onClick={() =>
                      setPerms((prev) => ({
                        ...prev,
                        [row.key]: ((prev[row.key] ?? row.def) + 1) % 3,
                      }))
                    }
                  >
                    {perm.label} ▾
                  </button>
                </div>
              );
            })}
          </div>
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="addresses"
          title="Article addresses"
          description="Where articles live on the site. Publishes with the theme; old addresses keep working and send readers to the new ones."
        >
          <div className="ink-site-fields">
            <label className="ink-site-label">
              Article address
              <input
                className="ink-site-input"
                value={articleBase}
                placeholder="article"
                spellCheck={false}
                onChange={(e) => setArticleBase(e.target.value.toLowerCase())}
                onBlur={() => void saveArticleBase()}
              />
            </label>
          </div>
          <p className="ink-site-hint">
            {(() => {
              const base = shownArticleBase || 'article';
              const list = shownArticleBase ? `/${shownArticleBase}` : '/articles';
              return `Articles live at ${host || ''}/${base}/article-name, and the list of all of them at ${host || ''}${list}. An article with a primary collection lives under it instead, like /${site?.working?.collections?.[0]?.slug || 'reviews'}/article-name.`;
            })()}
          </p>
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="domain"
          title="Domain"
          description="The web address people use to reach this site."
        >
          <DomainConnect />
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="search"
          title="Search engines"
          description="Whether Google and other search engines may list this site. Changes apply now; pages already listed drop out over a few weeks."
        >
          {/* Only a public site can be listed: until then the choice waits, greyed. */}
          {savedVis !== 'public' ? (
            <p className="sc-hidden-note">
              {savedVis === 'password'
                ? 'Search engines can’t reach a password-protected site, so nothing is listed. This applies again when the site is Public.'
                : 'The site is offline, so nothing is listed. This applies again when the site is Public.'}
            </p>
          ) : null}
          <div className="ink-site-choices" data-off={savedVis !== 'public'}>
            {(
              [
                [
                  'allow',
                  'Allow',
                  'Pages can appear in search results. We keep the sitemap submitted.',
                ],
                ['block', 'Block', 'Every page asks search engines not to list it. No sitemap.'],
              ] as const
            ).map(([key, label, desc]) => (
              <button
                key={key}
                type="button"
                className="ink-site-choice"
                data-on={
                  savedVis === 'public' &&
                  (account?.publicSearchBlocked ? 'block' : 'allow') === key
                }
                disabled={savedVis !== 'public'}
                onClick={() => void chooseSearch(key)}
              >
                <span className="ink-site-choice-title">
                  {label}
                  <span className="ink-site-radio" />
                </span>
                <span className="ink-site-choice-desc">{desc}</span>
              </button>
            ))}
          </div>
          {savedVis === 'public' ? (
            <div style={{ marginTop: 20 }}>
              <SearchConsole blocked={Boolean(account?.publicSearchBlocked)} />
            </div>
          ) : null}
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="ai-assistants"
          title="AI assistants"
          description="How AI assistants read your pages, and whether AI companies may train on them. Changes reach them within about a day."
        >
          {savedVis !== 'public' ? (
            <p className="sc-hidden-note">
              {savedVis === 'password'
                ? 'AI assistants can’t read a password-protected site. This applies again when the site is Public.'
                : 'The site is offline, so AI assistants can’t read it. This applies again when the site is Public.'}
            </p>
          ) : null}
          <div className="ink-site-choices" data-off={savedVis !== 'public'}>
            {(
              [
                [
                  'allow',
                  'Allow all',
                  'They can read your pages to answer questions and to train on them.',
                ],
                [
                  'answers',
                  'Answers only',
                  'They can read your pages to answer questions. AI companies are asked not to train on them.',
                ],
                [
                  'block',
                  'Block all',
                  'The AI assistants we know of are asked not to read your site.',
                ],
              ] as const
            ).map(([key, label, desc]) => (
              <button
                key={key}
                type="button"
                className="ink-site-choice"
                data-on={savedVis === 'public' && (account?.publicAiCrawlers || 'allow') === key}
                disabled={savedVis !== 'public'}
                onClick={() => void chooseAi(key)}
              >
                <span className="ink-site-choice-title">
                  {label}
                  <span className="ink-site-radio" />
                </span>
                <span className="ink-site-choice-desc">{desc}</span>
              </button>
            ))}
          </div>
          {savedVis === 'public' ? (
            <p className="ink-site-ai-note">
              This is a request, not a lock. Most AI companies say they follow it, but an assistant
              opening a page because someone asked for it may not. Google has one setting for
              training and for Gemini’s answers, so Answers only keeps your pages out of both.
              Google’s AI answers in Search follow Search engines above.
            </p>
          ) : null}
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="history"
          title="History"
          description="Every change on this page, by people and agents. Verification, ads.txt and Analytics & code versions can be restored."
        >
          <SettingsHistory />
        </InkSection>
      ) : null}

      {design ? (
        <InkSection title="History" description="Every published theme, with who approved it.">
          <ApprovalHistory
            kind="site"
            subjectId={accountId || undefined}
            variant="ink"
            onRestored={afterRestore}
            refreshKey={liveVersion}
            workingDiffers={unpublished}
          />
        </InkSection>
      ) : null}
    </div>
  );
};

/**
 * A site with no live version yet, drawn from its working tokens: the header,
 * a headline and three tiles — a sketch, since there is nothing real to frame.
 */
function OwnLookThumb({
  form,
  siteTitle,
  tagline,
}: {
  form: SiteForm;
  siteTitle: string;
  tagline: string;
}) {
  const radius = form.layout.corners === 'square' ? 0 : form.layout.corners === 'soft' ? 4 : 999;
  const accent = form.colors.accent || '#111';
  const muted = form.colors.muted || '#777';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: 190,
        padding: '14px 16px',
        background: form.colors.background || '#fff',
        color: form.colors.text || '#111',
        fontFamily: fontStack(form.fonts.body, 'sans'),
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ font: `600 11px ${fontStack(form.fonts.heading, 'serif')}` }}>
          {siteTitle}
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 6, color: muted }}>
          <span>About</span>
          <span>Contact</span>
          <span
            style={{ padding: '2px 6px', borderRadius: radius, background: accent, color: '#fff' }}
          >
            Get in touch
          </span>
        </span>
      </span>
      <span
        style={{ font: `400 20px/1.05 ${fontStack(form.fonts.heading, 'serif')}`, maxWidth: '85%' }}
      >
        {tagline || 'Your headline, in your own words.'}
      </span>
      <span
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 'auto' }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              height: 34,
              borderRadius: Math.min(radius, 8),
              background: `color-mix(in srgb, ${muted} 22%, ${form.colors.background || '#fff'})`,
            }}
          />
        ))}
      </span>
    </span>
  );
}
